import moment from 'moment-timezone';
import constants from '../../shared/constants/constants';

const CanvasTextCoordinates = Object.freeze({
  X: 30,
  Y: 100
});

const Mode = Object.freeze(  {
    TYPE: 'type',
    DRAW: 'draw'
});

const Font = Object.freeze({
  LARGE: "40px Allura",
  MEDIUM: "30px Allura",
  SMALL: "22px Allura"
});

class SignatureValue {
  constructor({ userFullName, signedAt, fileUrl }) {
    const hasRequiredFields = [ userFullName, signedAt, fileUrl ].every(Boolean);

    if (!hasRequiredFields) {
      throw new Error('Error signing form without a required field');
    }

    this.userFullName = userFullName;
    this.signedAt = signedAt;
    this.fileUrl = fileUrl;
  }
}

export default class Signature {
  constructor() {
    this.canvas = document.getElementById('signature-canvas');

    if (this.canvas == null) {
      return;
    }

    this.signatureValue = {}
    this.currentMode = Mode.TYPE;

    this.websiteTimeZoneId = window.edlio.websiteTimeZoneId;
    this.currentSchoolTzDate = moment.tz(Date.now(), this.websiteTimeZoneId);

    this.getElements();
    this.initModalWindow();
    this.initCanvas();
    this.addListeners();

    this.manageSaveButtonStatus();
    this.manageFormElementsVisibility();
    this.signatureDateText.textContent = this._getCurrentDateText(this.currentSchoolTzDate);
  }

  getElements () {
    this.signatureNameInput = document.getElementById('signature-name-input');
    this.signatureClearButton = document.getElementById('signature-clear-button');
    this.signatureSaveButton = document.getElementById('signature-save-button');
    this.signatureCancelButton = document.getElementById('signature-cancel-button');
    this.signatureDateText = document.getElementById('signature-date-text');
    this.signatureTypeModeButton = document.getElementById('signature-type-mode-button');
    this.signatureDrawModeButton = document.getElementById('signature-draw-mode-button');
    this.signatureCanvasTypePlaceholder = document.getElementById('signature-canvas-type-placeholder');
    this.signatureCanvasDrawPlaceholder = document.getElementById('signature-canvas-draw-placeholder');

    this.container = document.getElementById('form');
    this.signatureTextDataWrapper = document.getElementById('signature-text-data-wrapper');
    this.signatureFormImage = document.getElementById('signature-form-image');
    this.signatureFormFullName = document.getElementById('signature-form-full-name');
    this.signatureFormDateText = document.getElementById('signature-form-date-text');
    this.openSignatureModalButton = document.getElementById('open-signature-modal-button');
  }

  initModalWindow () {
    const me = this;

    (function($) {
      $(document).ready(function() {
        const $targetElements = $('#open-signature-modal-button, #signature-image-element, #signature-input-label');

        $targetElements.magnificPopup({
          closeOnBgClick: false,
          items: {
            src: '#signature-modal',
            type: 'inline'
          },
          callbacks: {
            open: me.handleModalOpen.bind(me)
          }
        });
      });

    })(jq37);
  }

  initCanvas () {
    this.signaturePad = new SignaturePad(this.canvas);

    this.ctx = this.canvas.getContext("2d");
    this.ctx.font = Font.LARGE;
    this.ctx.fillText('', CanvasTextCoordinates.X, CanvasTextCoordinates.Y);

    this.signaturePad.off();
  }

  addListeners () {
    const me = this;

    me.signatureClearButton.addEventListener('click', function () {
      me._clearCanvas();
      me.manageSaveButtonStatus();
      me.manageFormElementsVisibility();
    });

    me.signatureNameInput.addEventListener('keyup', function () {
      if (me.currentMode === Mode.TYPE) {
        me._renderNameOnCanvas();
      }

      me.manageSaveButtonStatus();
      me.manageFormElementsVisibility();
    });

    me.signatureSaveButton.addEventListener('click', function () {
      me.save()
    });

    me.signatureCancelButton.addEventListener('click', function () {
      me._closeModal();
    });

    me.signatureTypeModeButton.addEventListener('click', function () {
      me.changeMode(Mode.TYPE)
    });

    me.signatureDrawModeButton.addEventListener('click', function () {
      me.changeMode(Mode.DRAW)
    });

    me.signaturePad.addEventListener("beginStroke", () => {
      if (me.currentMode === Mode.DRAW) {
        me.signatureCanvasDrawPlaceholder.classList.remove('active');
      }
    });

    me.signaturePad.addEventListener("endStroke", () => {
      me.manageSaveButtonStatus();
      me.manageFormElementsVisibility();
    });
  }

  handleModalOpen() {
    this._setCanvasSize();
    this._resetForm();
  }

  changeMode (newMode) {
    if (newMode !== this.currentMode) {
      if (newMode === Mode.TYPE) {
        this.signatureTypeModeButton.classList.add("active");
        this.signatureDrawModeButton.classList.remove("active");
        this.signatureClearButton.classList.add('hidden');

        this.signaturePad.off();
        this._renderNameOnCanvas();
      }

      if (newMode === Mode.DRAW) {
        this.signatureDrawModeButton.classList.add("active");
        this.signatureTypeModeButton.classList.remove("active");

        this._clearCanvas();
        this.signaturePad.on();
      }

      this.currentMode = newMode;
      this.manageSaveButtonStatus();
      this.manageFormElementsVisibility();
    }
  }

  manageSaveButtonStatus () {
    if (this._isFormValid()) {
      this.signatureSaveButton.classList.remove('disabled');
    } else {
      this.signatureSaveButton.classList.add('disabled');
    }
  }

  manageFormElementsVisibility () {
    const isCanvasNotBlank = this._isCanvasNotBlank();

    if (this.currentMode === Mode.DRAW) {
      this.signatureCanvasTypePlaceholder.classList.remove('active');

      if (isCanvasNotBlank) {
        this.signatureClearButton.classList.remove('hidden');
        this.signatureCanvasDrawPlaceholder.classList.remove('active');
      } else {
        this.signatureClearButton.classList.add('hidden');
        this.signatureCanvasDrawPlaceholder.classList.add('active');
      }
    }

    if (this.currentMode === Mode.TYPE) {
      this.signatureCanvasDrawPlaceholder.classList.remove('active');

      if (isCanvasNotBlank) {
        this.signatureCanvasTypePlaceholder.classList.remove('active');
      } else {
        this.signatureCanvasTypePlaceholder.classList.add('active');
      }
    }
  }

  save () {
    if (this._isFormValid()) {
      const userFullName = this._getTrimmedSignatureNameValue();
      const signedAt = this.currentSchoolTzDate;
      const fileUrl = this.signaturePad.toDataURL('image/png');

      this.signatureValue = new SignatureValue({
        userFullName,
        signedAt,
        fileUrl
      })

      const signatureSaveEvent = new CustomEvent('signatureSave');

      this.fillMainFormFields(this.signatureValue);
      this.container.dispatchEvent(signatureSaveEvent);
      this._closeModal();
    }
  }

  getSignatureValue () {
    return this.signatureValue;
  }

  fillMainFormFields (signatureValue) {
    this.signatureFormImage.setAttribute('src', signatureValue.fileUrl);
    this.signatureFormFullName.textContent = signatureValue.userFullName;
    this.signatureFormDateText.textContent = this._getCurrentDateText(signatureValue.signedAt);

    this.signatureTextDataWrapper.classList.remove('hidden');
    this.openSignatureModalButton.classList.add('resign');
  }

  _resetForm () {
    this._clearCanvas();
    this.signatureNameInput.value = '';
    this.signatureSaveButton.classList.add('disabled');
    this.changeMode(Mode.TYPE);
  }

  _setCanvasSize() {
    this.canvas.width = this.canvas.offsetWidth;
    this.canvas.height = this.canvas.offsetHeight;
  }

  _clearCanvas () {
    this.signaturePad.clear();
  }

  _renderNameOnCanvas () {
    this._clearCanvas();

    const text = this._getTrimmedSignatureNameValue();
    this._setFontSize(text);

    this.ctx.fillText(text, CanvasTextCoordinates.X, CanvasTextCoordinates.Y);
  }

  _setFontSize (text) {
    const spaceForTextWidth = (this.canvas.width - CanvasTextCoordinates.X * 2);

    this.ctx.font = Font.LARGE;
    let textWidth = this._getTextWidth(text);

    if (textWidth > spaceForTextWidth) {
      this.ctx.font = Font.MEDIUM;
      textWidth = this._getTextWidth(text);

      if (textWidth > spaceForTextWidth) {
        this.ctx.font = Font.SMALL;
      }
    }
  }

  _getTextWidth (text) {
    const metrics = this.ctx.measureText(text);

    return metrics.width
  }

  _isCanvasNotBlank () {
    if (this.currentMode === Mode.DRAW) {
      return !this.signaturePad.isEmpty();
    }

    if (this.currentMode === Mode.TYPE) {
      return !!this._getTrimmedSignatureNameValue() && !this._containsOnlyForbiddenCharacters();
    }
  }

  _isFormValid () {
    return this._isCanvasNotBlank() && !!this._getTrimmedSignatureNameValue() && !this._containsOnlyForbiddenCharacters();
  }

  _closeModal () {
    jq37.magnificPopup.close();
  }

  _getCurrentDateText (timestamp) {
    return moment(timestamp).format(constants.dateFormat);
  }

  _getTrimmedSignatureNameValue() {
    return this.signatureNameInput.value.trim();
  }

  _containsOnlyForbiddenCharacters() {
    const forbiddenCharacters = ['&nbsp;', '&shy;', '&ensp;', '&emsp;', '&thinsp;', '&zwnj;', '&zwj;', '&lrm;', '&rlm;'];
    const forbiddenParsedCharacters = forbiddenCharacters.map((char) => new DOMParser().parseFromString(char, 'text/html').body.textContent);
    const sanitizedInput = forbiddenParsedCharacters.reduce((acc, char) => acc.split(char).join(''), this.signatureNameInput.value).trim();
    
    return sanitizedInput === '';
  }
}



// WEBPACK FOOTER //
// ./src/main/webapps/features/form-builder/react/public/classes/signature.js